/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { Calendar } from 'sharedUi/Calendar';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import ModelCredentialsPanel from './model-credentials-panel';
import {
  EMPTY_CREDENTIALS,
  createConnectionFor,
  isKeepingStored,
  specFor,
  useProviderSpecs,
  validateCredentials,
  type CredentialValue,
} from './model-credentials';

import { aiModelsService } from '../../services/ai-models.service';
import type { AiAvailableModel, AiConnectionTestResult } from '../../types';
import { projectsService } from '../../services/projects.service';
import { valueSetsService } from '../../services/value-sets.service';
import { connectionTypesService } from '../../services/connection-types.service';
import { extractErrorMessage } from '../../utils/error-handler';

// Fallbacks used only until the aiProviderType / aiModelType value sets are seeded.
const PROVIDER_OPTIONS = [
  { label: 'Anthropic', value: 'anthropic' },
  { label: 'OpenAI', value: 'openai' },
  { label: 'OpenRouter', value: 'openrouter' },
  { label: 'Google (Gemini API)', value: 'google' },
  { label: 'Google Vertex AI', value: 'vertex_ai' },
  { label: 'Azure OpenAI', value: 'azure_openai' },
  { label: 'Azure Document Intelligence', value: 'azure_document_intelligence' },
  { label: 'Bedrock', value: 'bedrock' },
  { label: 'Ollama', value: 'ollama' },
  { label: 'Custom', value: 'custom' },
];

const MODEL_TYPE_OPTIONS = [
  { label: 'Base', value: 'base' },
  { label: 'Fine-tuned', value: 'fine_tuned' },
  { label: 'Embedding', value: 'embedding' },
  { label: 'Document Analysis (OCR)', value: 'document_analysis' },
];

// Labels for the model types, used when the value set has not been seeded with
// a type the backend already serves — the provider spec, not the value set,
// decides which types exist.
// Mirrors TRAINING_STATUSES in 4s_core_be/app/api/v1/schemas/ai_model_schema.py.
const TRAINING_STATUS_OPTIONS = [
  { label: 'Queued', value: 'queued' },
  { label: 'Running', value: 'running' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Cancelled', value: 'cancelled' },
];

const MODEL_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  MODEL_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

// Zod Validation Schema
const aiModelSchema = z.object({
  providerType: z.string().min(1, 'Provider is required'),
  modelId: z
    .string()
    .min(1, 'Model ID is required')
    .max(200, 'Model ID must not exceed 200 characters')
    .trim(),
  displayName: z
    .string()
    .min(1, 'Display Name is required')
    .max(200, 'Display Name must not exceed 200 characters')
    .trim(),
  version: z.string().max(100, 'Version must not exceed 100 characters').optional().or(z.literal('')),
  description: z.string().max(1000, 'Description must not exceed 1000 characters').optional().or(z.literal('')),
  iconPath: z.string().max(500, 'Icon Path must not exceed 500 characters').optional().or(z.literal('')),
  modelType: z.string().optional().or(z.literal('')),
  connectionId: z.string().optional().or(z.literal('')),
  // Capabilities
  maxContextTokens: z.number().nullable().optional(),
  maxOutputTokens: z.number().nullable().optional(),
  supportsSystemPrompt: z.boolean().optional(),
  supportsTools: z.boolean().optional(),
  supportsStreaming: z.boolean().optional(),
  supportsJsonMode: z.boolean().optional(),
  supportsVision: z.boolean().optional(),
  // Pricing
  currency: z.string().max(10, 'Currency must not exceed 10 characters').optional().or(z.literal('')),
  inputPerM: z.number().nullable().optional(),
  outputPerM: z.number().nullable().optional(),
  cacheReadPerM: z.number().nullable().optional(),
  cacheWritePerM: z.number().nullable().optional(),
  perThousandPages: z.number().nullable().optional(),
  // Flags
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  isDeprecated: z.boolean().optional(),
  appId: z.string().trim().optional().or(z.literal('')),
  // Parameters (paramsSchema) — a dynamic list serialized to a paramsSchema object.
  paramsRows: z
    .array(
      z.object({
        name: z.string().optional().or(z.literal('')),
        default: z.string().optional().or(z.literal('')),
        min: z.string().optional().or(z.literal('')),
        max: z.string().optional().or(z.literal('')),
        enum: z.string().optional().or(z.literal('')),
      })
    )
    .optional(),
  // Fine-tune lineage (training.*) — only sent for a fine_tuned model.
  baseModelId: z.string().optional().or(z.literal('')),
  fineTuneJobId: z.string().max(200, 'Job ID must not exceed 200 characters').optional().or(z.literal('')),
  datasetRef: z.string().max(500, 'Dataset must not exceed 500 characters').optional().or(z.literal('')),
  trainedAt: z.date().nullable().optional(),
  trainingStatus: z.string().optional().or(z.literal('')),
}).refine((d) => d.modelType !== 'fine_tuned' || !!d.baseModelId, {
  message: 'Pick the model this was fine-tuned from',
  path: ['baseModelId'],
});

// ── paramsSchema (de)serialization ────────────────────────────────
interface ParamRow {
  name: string;
  default: string;
  min: string;
  max: string;
  enum: string;
}

// A numeric-looking string → number, otherwise the trimmed string (or undefined).
const coerceValue = (raw: string): number | string | undefined => {
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) && t === String(n) ? n : t;
};

const numberOrUndef = (raw: string): number | undefined => {
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

/** Rows → paramsSchema object (BE shape). Skips rows without a name. */
const rowsToParamsSchema = (rows: ParamRow[]): Record<string, any> => {
  const out: Record<string, any> = {};
  rows.forEach((r) => {
    const name = (r.name || '').trim();
    if (!name) return;
    const enumVals = (r.enum || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (enumVals.length > 0) {
      out[name] = { default: coerceValue(r.default), enum: enumVals };
    } else {
      const spec: Record<string, any> = { default: coerceValue(r.default) };
      const min = numberOrUndef(r.min);
      const max = numberOrUndef(r.max);
      if (min !== undefined) spec.min = min;
      if (max !== undefined) spec.max = max;
      out[name] = spec;
    }
  });
  return out;
};

/** paramsSchema object → rows for the editor. */
const paramsSchemaToRows = (schema?: Record<string, any> | null): ParamRow[] => {
  if (!schema || typeof schema !== 'object') return [];
  return Object.entries(schema).map(([name, raw]) => {
    const spec = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
    return {
      name,
      default: spec.default === undefined || spec.default === null ? '' : String(spec.default),
      min: spec.min === undefined || spec.min === null ? '' : String(spec.min),
      max: spec.max === undefined || spec.max === null ? '' : String(spec.max),
      enum: Array.isArray(spec.enum) ? spec.enum.join(', ') : '',
    };
  });
};

const CLAUDE_PRESET: ParamRow[] = [
  { name: 'maxTokens', default: '16000', min: '1', max: '128000', enum: '' },
  { name: 'effort', default: 'high', min: '', max: '', enum: 'low,medium,high,xhigh,max' },
];

const GEMINI_PRESET: ParamRow[] = [
  { name: 'maxTokens', default: '8192', min: '1', max: '65536', enum: '' },
  { name: 'temperature', default: '1', min: '0', max: '2', enum: '' },
  { name: 'topP', default: '0.95', min: '0', max: '1', enum: '' },
  { name: 'topK', default: '40', min: '1', max: '', enum: '' },
];

const OPENAI_PRESET: ParamRow[] = [
  { name: 'maxTokens', default: '4096', min: '1', max: '16384', enum: '' },
  { name: 'temperature', default: '1', min: '0', max: '2', enum: '' },
  { name: 'topP', default: '1', min: '0', max: '1', enum: '' },
];

type AiModelFormData = z.infer<typeof aiModelSchema>;

const AiModelForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'ai-model',
    basePath: '/ai-models',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors, isDirty },
  } = useForm<AiModelFormData>({
    resolver: zodResolver(aiModelSchema),
    defaultValues: {
      providerType: '',
      modelId: '',
      displayName: '',
      version: '',
      description: '',
      iconPath: '',
      modelType: '',
      connectionId: '',
      maxContextTokens: null,
      maxOutputTokens: null,
      supportsSystemPrompt: false,
      supportsTools: false,
      supportsStreaming: false,
      supportsJsonMode: false,
      supportsVision: false,
      currency: '',
      inputPerM: null,
      outputPerM: null,
      cacheReadPerM: null,
      cacheWritePerM: null,
      perThousandPages: null,
      isDefault: false,
      isActive: true,
      isDeprecated: false,
      appId: '',
      paramsRows: [],
      baseModelId: '',
      fineTuneJobId: '',
      datasetRef: '',
      trainedAt: null,
      trainingStatus: '',
    },
    mode: 'onChange',
  });

  const {
    fields: paramFields,
    append: appendParam,
    remove: removeParam,
    replace: replaceParams,
  } = useFieldArray({ control, name: 'paramsRows' });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // Credentials live outside react-hook-form: the fields depend on the selected
  // provider, so the shape is not known when the resolver is built.
  const [credentials, setCredentials] = useState<CredentialValue>(EMPTY_CREDENTIALS);
  const [credentialErrors, setCredentialErrors] = useState<Record<string, string>>({});
  const [credentialsDirty, setCredentialsDirty] = useState(false);

  const { data: providerSpecs } = useProviderSpecs();
  const selectedProvider = watch('providerType');
  const activeSpec = specFor(providerSpecs, selectedProvider);

  const selectedModelType = watch('modelType');
  const isFineTuned = selectedModelType === 'fine_tuned';
  const isEmbedding = selectedModelType === 'embedding';
  // Which model types this provider can serve. Document Intelligence serves only
  // document analysis; offering "Base" for it would save a model that appears in
  // every prompt picker and fails on its first run. Null for a backend that does
  // not send modelTypes yet, which keeps the old embedding-only rule below.
  const allowedModelTypes: string[] | null = activeSpec?.modelTypes?.length
    ? activeSpec.modelTypes
    : null;
  const isDocument = selectedModelType === 'document_analysis'
    || (allowedModelTypes?.length === 1 && allowedModelTypes[0] === 'document_analysis');

  // Anthropic publishes no embedding model — it points at Voyage AI — so this
  // combination is not a gap a credential could close. The selection is cleared
  // rather than only flagged, so the form cannot submit a pairing no provider
  // is able to serve.
  // Result of the last live provider call from this form. Kept in state rather
  // than shown as a toast: it is the answer to a question the operator asked,
  // and it should stay on screen while they fix the field it points at.
  const [connTest, setConnTest] = useState<AiConnectionTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // What the provider listed for the credential below. Null until asked; a list
  // belongs to one provider, so switching provider drops it.
  const [available, setAvailable] = useState<AiAvailableModel[] | null>(null);
  const [listing, setListing] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  useEffect(() => { setAvailable(null); setListError(null); }, [selectedProvider]);

  // A type the provider cannot serve is cleared rather than only flagged, so the
  // form cannot submit a pairing no credential could make work. A provider with
  // exactly one type gets it selected for them.
  const embeddingUnsupported = isEmbedding && activeSpec?.supportsEmbeddings === false;
  const typeUnsupported = !!allowedModelTypes && !!selectedModelType
    && !allowedModelTypes.includes(selectedModelType);
  const onlyType = allowedModelTypes?.length === 1 ? allowedModelTypes[0] : null;
  useEffect(() => {
    if (onlyType && selectedModelType !== onlyType) {
      setValue('modelType', onlyType, { shouldDirty: true });
    } else if (embeddingUnsupported || typeUnsupported) {
      setValue('modelType', '', { shouldDirty: true });
    }
  }, [onlyType, selectedModelType, embeddingUnsupported, typeUnsupported, setValue]);

  // A green tick against a key that has since been edited is worse than no
  // result at all, so the last result is dropped whenever its inputs change.
  useEffect(() => {
    setConnTest(null);
  }, [selectedProvider, credentials.secret, credentials.connectionId, credentials.config]);

  const { data: connectionTypes = [] } = useQuery({
    queryKey: ['connection-types', 'dropdown'],
    queryFn: () => connectionTypesService.getAllForDropdown(),
    staleTime: 5 * 60 * 1000,
  });

  // Provider + model-type options from value sets (admin-editable via the
  // Value Sets screen), falling back to the built-in lists until seeded.
  const { data: providerVS } = useQuery({
    queryKey: ['value-sets', 'aiProviderType'],
    queryFn: () => valueSetsService.getByKey('aiProviderType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  // The served specs are the authority: a provider offered here but absent from
  // them has no adapter and no credential fields, so it could only ever produce
  // a model that fails on first use. The value set and the static list remain as
  // fallbacks for when the endpoint has not answered yet.
  const providerOptions = providerSpecs?.length
    ? providerSpecs.map((sp) => ({ value: sp.providerType, label: sp.label }))
    : providerVS?.items?.length
      ? providerVS.items.map((i) => ({ value: i.code, label: i.label }))
      : PROVIDER_OPTIONS;

  const { data: modelTypeVS } = useQuery({
    queryKey: ['value-sets', 'aiModelType'],
    queryFn: () => valueSetsService.getByKey('aiModelType', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  const vsLabels: Record<string, string> = Object.fromEntries(
    (modelTypeVS?.items || []).map((i) => [i.code, i.label]),
  );
  const modelTypeOptions = allowedModelTypes
    ? allowedModelTypes.map((t) => ({ value: t, label: vsLabels[t] || MODEL_TYPE_LABELS[t] || t }))
    : (modelTypeVS?.items?.length
      ? modelTypeVS.items.map((i) => ({ value: i.code, label: i.label }))
      : MODEL_TYPE_OPTIONS
    ).filter((o) => o.value !== 'embedding' || activeSpec?.supportsEmbeddings !== false)
      .filter((o) => o.value !== 'document_analysis');

  // Chat models a fine-tune can descend from. Not this row itself.
  const { data: baseModelsResp } = useQuery({
    queryKey: ['ai-models', 'options', 'runnable'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100, runnable_only: true }),
    enabled: isFineTuned,
    staleTime: 5 * 60 * 1000,
  });
  const baseModels = (baseModelsResp?.data || []).filter((m) => m._id !== id);
  const baseModelOptions = baseModels.map((m) => ({
    value: m._id,
    label: `${m.displayName} · ${m.providerType}`,
  }));

  // A fine-tune keeps its parent's context window and features, so picking the
  // parent fills whatever capability the operator has not set yet.
  const pickBaseModel = (baseId: string) => {
    const opts = { shouldDirty: true };
    setValue('baseModelId', baseId, { ...opts, shouldValidate: true });
    const caps = baseModels.find((m) => m._id === baseId)?.capabilities;
    if (!caps) return;
    if (getValues('maxContextTokens') == null && caps.maxContextTokens != null) setValue('maxContextTokens', caps.maxContextTokens, opts);
    if (getValues('maxOutputTokens') == null && caps.maxOutputTokens != null) setValue('maxOutputTokens', caps.maxOutputTokens, opts);
    const flags = ['supportsSystemPrompt', 'supportsTools', 'supportsStreaming', 'supportsJsonMode', 'supportsVision'] as const;
    if (flags.every((f) => !getValues(f))) flags.forEach((f) => setValue(f, !!caps[f], opts));
  };

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({
    when: isDirty || credentialsDirty,
  });

  // Fetch model data for edit mode - wait for ID decryption
  const { data: model, isLoading: fetchLoading } = useQuery({
    queryKey: ['ai-models', id],
    queryFn: () => aiModelsService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show model name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? model?.displayName : null);

  // Populate form when model data is loaded
  useEffect(() => {
    if (model && isEditMode) {
      reset({
        providerType: model.providerType || '',
        modelId: model.modelId || '',
        displayName: model.displayName || '',
        version: model.version || '',
        description: model.description || '',
        iconPath: model.iconPath || '',
        modelType: model.modelType || '',
        connectionId: model.connectionId || '',
        maxContextTokens: model.capabilities?.maxContextTokens ?? null,
        maxOutputTokens: model.capabilities?.maxOutputTokens ?? null,
        supportsSystemPrompt: !!model.capabilities?.supportsSystemPrompt,
        supportsTools: !!model.capabilities?.supportsTools,
        supportsStreaming: !!model.capabilities?.supportsStreaming,
        supportsJsonMode: !!model.capabilities?.supportsJsonMode,
        supportsVision: !!model.capabilities?.supportsVision,
        currency: model.pricing?.currency || '',
        inputPerM: model.pricing?.inputPerM ?? null,
        outputPerM: model.pricing?.outputPerM ?? null,
        cacheReadPerM: model.pricing?.cacheReadPerM ?? null,
        cacheWritePerM: model.pricing?.cacheWritePerM ?? null,
        perThousandPages: model.pricing?.perThousandPages ?? null,
        isDefault: !!model.isDefault,
        isActive: model.isActive ?? true,
        isDeprecated: !!model.isDeprecated,
        appId: (model as any).appId || '',
        paramsRows: paramsSchemaToRows(model.paramsSchema),
        baseModelId: model.training?.baseModelId || '',
        fineTuneJobId: model.training?.fineTuneJobId || '',
        datasetRef: model.training?.datasetRef || '',
        trainedAt: model.training?.trainedAt ? new Date(model.training.trainedAt) : null,
        trainingStatus: model.training?.status || '',
      });
      // A saved row either binds a connection or is served by the deployment.
      // Editing never pre-fills a secret — the backend does not return one.
      setCredentials({
        ...EMPTY_CREDENTIALS,
        connectionId: model.connectionId || '',
      });
      setCredentialsDirty(false);
    }
  }, [model, isEditMode, reset]);

  // Mutation
  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditMode && id) {
        return aiModelsService.update(id, data);
      }
      return aiModelsService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-models'] });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `AI model ${isEditMode ? 'updated' : 'created'} successfully`,
        life: 3000,
      });
      setTimeout(() => {
        navigateToList();
      }, 1000);
    },
    onError: (error: any) => {
      const errorMessage = extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} AI model`);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  const isSaving = mutation.isPending;

  /**
   * Check the credentials against the provider before anything is written.
   *
   * Runs as part of saving rather than from a button of its own: the operator's
   * intent is "create this model", and whether the key works is part of whether
   * that succeeded. Silent when it passes — the model is simply created.
   *
   * Deliberately BEFORE the connection is created, so a rejected key leaves no
   * orphaned partner_connections row behind and nothing to clean up.
   *
   * On edit with no retyped secret the stored credential is tested through
   * connectionId, since the backend never returns a secret to the browser.
   */
  const checkCredentials = async (modelId: string): Promise<AiConnectionTestResult> => {
    setTesting(true);
    setConnTest(null);
    try {
      const result = await aiModelsService.testConnection({
        providerType: selectedProvider as never,
        modelId,
        modelType: selectedModelType || null,
        connectionId: credentials.connectionId || null,
        apiKey: credentials.secret.trim() || null,
        connectionConfig: credentials.config,
      });
      if (!result.ok) {
        setConnTest(result);
        // The banner says what went wrong; this puts it on the input that caused
        // it. Without it an operator reads "could not connect" and re-checks the
        // key, when the unreachable host is two fields down.
        const field = result.field;
        if (field) {
          const short = (result.summary || '').split('. ')[0] || 'This looks wrong';
          if (field === 'modelId') setError('modelId', { type: 'server', message: short });
          else setCredentialErrors((prev) => ({ ...prev, [field]: short }));
        }
      }
      return result;
    } catch (error) {
      // The check itself failing is not proof the credentials are bad, so it
      // does not block the save — saying otherwise would make a flaky network
      // look like a rejected key.
      return {
        ok: true, latencyMs: null, reply: null,
        error: extractErrorMessage(error, 'The check could not be run'),
      };
    } finally {
      setTesting(false);
    }
  };

  /** Ask the provider what this credential can use. Free — metadata only. */
  const fetchModels = async () => {
    setListing(true);
    setListError(null);
    try {
      const result = await aiModelsService.listAvailable({
        providerType: selectedProvider as never,
        connectionId: credentials.connectionId || null,
        apiKey: credentials.secret.trim() || null,
        connectionConfig: credentials.config,
      });
      if (!result.ok) {
        setAvailable(null);
        setListError(result.summary || result.error || 'The provider did not list any models.');
        const field = result.field;
        if (field && field !== 'modelId') {
          const short = (result.summary || '').split('. ')[0] || 'This looks wrong';
          setCredentialErrors((prev) => ({ ...prev, [field]: short }));
        }
        return;
      }
      // Only types this provider can be saved with; the rest could never be picked.
      const models = allowedModelTypes
        ? result.models.filter((m) => allowedModelTypes.includes(m.modelType))
        : result.models;
      setAvailable(models);
      if (!models.length) setListError('The provider listed no models this form can use.');
    } catch (error) {
      setListError(extractErrorMessage(error, 'Could not list models'));
    } finally {
      setListing(false);
    }
  };

  /**
   * Fill the form from a listed model. The id always; everything else only
   * where the field is still empty, so nothing the operator typed is replaced.
   */
  const pickModel = (id: string) => {
    const m = available?.find((x) => x.id === id);
    if (!m) return;
    const opts = { shouldDirty: true, shouldValidate: true };
    setValue('modelId', m.id, opts);
    if (!getValues('displayName')?.trim()) setValue('displayName', m.name, opts);
    if (!getValues('modelType')) setValue('modelType', m.modelType, opts);
    if (m.contextWindow && getValues('maxContextTokens') == null) {
      setValue('maxContextTokens', m.contextWindow, opts);
    }
    const price = m.pricing;
    if (price) {
      if (!getValues('currency')) setValue('currency', price.currency, opts);
      (['inputPerM', 'outputPerM', 'cacheReadPerM', 'cacheWritePerM'] as const).forEach((k) => {
        if (price[k] != null && getValues(k) == null) setValue(k, price[k], opts);
      });
    }
  };

  const typeLabel = (t: string) => ({ base: 'Chat', embedding: 'Embedding', document_analysis: 'Document' }[t] || t);

  const onSubmit = async (data: AiModelFormData) => {
    // Credentials are validated here rather than by the zod resolver: which
    // fields exist depends on the provider, which is not known when the schema
    // is built. The backend re-checks — this only moves the message to the field.
    const credErrors = validateCredentials(credentials, activeSpec);
    setCredentialErrors(credErrors);
    if (Object.keys(credErrors).length) {
      toast.current?.show({
        severity: 'warn', summary: 'Credentials incomplete',
        detail: Object.values(credErrors)[0], life: 6000,
      });
      return;
    }

    // Silent credential check, before anything is written. A key the provider
    // rejects stops the save here — the alternative is a model row that saves
    // cleanly and 503s on its first prompt run, days later, with no obvious
    // cause.
    const check = await checkCredentials(data.modelId.trim());
    if (!check.ok) {
      toast.current?.show({
        severity: 'error',
        summary: 'Credentials rejected',
        detail: 'Nothing was saved. See the error below the form.',
        life: 10000,
      });
      return;
    }

    // Editing without retyping the secret keeps the credential already bound.
    // Otherwise the connection is created FIRST, so a failure here leaves no
    // model pointing at nothing.
    let connectionId: string | null = null;
    if (isKeepingStored(credentials)) {
      connectionId = credentials.connectionId;
    } else if (activeSpec) {
      try {
        // In the MODEL's project (the form's appId, set from the row on edit),
        // not the caller's token project the model could never resolve it in.
        connectionId = await createConnectionFor(activeSpec, credentials, connectionTypes, data.appId || undefined);
      } catch (error: any) {
        toast.current?.show({
          severity: 'error', summary: 'Could not save the credentials',
          detail: extractErrorMessage(error, 'The connection could not be created'),
          life: 10000,
        });
        return;
      }
    }

    const payload: any = {
      providerType: data.providerType,
      modelId: data.modelId,
      displayName: data.displayName,
      version: data.version || null,
      description: data.description || null,
      iconPath: data.iconPath || null,
      modelType: data.modelType || null,
      connectionId,
      // A document model has no token limits or chat features to declare, so
      // none are sent rather than a row of false flags that reads as a claim.
      capabilities: isDocument ? null : {
        maxContextTokens: data.maxContextTokens ?? null,
        maxOutputTokens: data.maxOutputTokens ?? null,
        supportsSystemPrompt: !!data.supportsSystemPrompt,
        supportsTools: !!data.supportsTools,
        supportsStreaming: !!data.supportsStreaming,
        supportsJsonMode: !!data.supportsJsonMode,
        supportsVision: !!data.supportsVision,
      },
      pricing: isDocument ? {
        currency: data.currency || null,
        perThousandPages: data.perThousandPages ?? null,
      } : {
        currency: data.currency || null,
        inputPerM: data.inputPerM ?? null,
        outputPerM: data.outputPerM ?? null,
        cacheReadPerM: data.cacheReadPerM ?? null,
        cacheWritePerM: data.cacheWritePerM ?? null,
      },
      isDefault: !!data.isDefault,
      isActive: !!data.isActive,
      isDeprecated: !!data.isDeprecated,
      paramsSchema: rowsToParamsSchema((data.paramsRows || []) as ParamRow[]),
      training: isFineTuned ? {
        baseModelId: data.baseModelId || null,
        fineTuneJobId: data.fineTuneJobId?.trim() || null,
        datasetRef: data.datasetRef?.trim() || null,
        trainedAt: data.trainedAt ? data.trainedAt.toISOString() : null,
        status: data.trainingStatus || null,
      } : null,
    };

    // Owning project (honoured by the BE only for callers who can access it).
    // Create-only field.
    if (!isEditMode && data.appId) payload.appId = data.appId;

    try {
      mutation.mutate(payload);
    } catch (error) {
      console.error('Error calling mutation.mutate:', error);
    }
  };

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-AiModel-Form">
        {/* Header */}
        <ScreenHeader
          icon="pi pi-database"
          title={isEditMode ? 'Edit AI Model' : 'Create New AI Model'}
          subtitle={isEditMode ? 'Update AI model configuration' : 'Register a new AI model'}
          onBack={() => handleNavigate('/ai-models')}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Project Scope */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-sitemap text-blue-600" />
                <span className="text-sm font-medium text-ink">Project Scope</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <Controller
                    name="appId"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormSelect
                        {...field}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        label="Project"
                        options={projectOptions}
                        placeholder="Select project"
                        disabled={projectLocked || isEditMode}
                        error={!!errors.appId}
                        helperText={errors.appId?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-info-circle text-blue-600" />
                <span className="text-sm font-medium text-ink">Basic Information</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Provider <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="providerType"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value || null}
                        onChange={(e: { value: string }) => field.onChange(e.value || '')}
                        options={providerOptions}
                        placeholder="Select provider"
                        filter
                      />
                    )}
                  />
                  {errors.providerType && (
                    <span className="text-xs text-red-500">{errors.providerType.message}</span>
                  )}
                  {activeSpec?.hint ? (
                    <p className="text-xs text-ink-muted leading-snug">{activeSpec.hint}</p>
                  ) : (
                    <p className="text-xs text-ink-muted leading-snug">
                      OpenAI-compatible endpoint (returns <code>choices[]</code>): pick <b>openai</b> if it
                      needs an API key, or <b>ollama</b> if it needs none (local / self-host / tunnel).
                      Use <b>custom</b> only for a non-standard API (you must set its response <code>textPath</code>).
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {activeSpec?.modelIdLabel || 'Model ID'} <span className="text-red-500">*</span>
                    </label>
                    {selectedProvider && (
                      <Button
                        type="button"
                        label={listing ? 'Fetching…' : 'Fetch models'}
                        icon="pi pi-list"
                        className="p-button-text p-button-sm"
                        onClick={fetchModels}
                        disabled={listing}
                        data-testid="AiModelForm.FetchModels"
                      />
                    )}
                  </div>
                  {available && available.length > 0 && (
                    <Dropdown
                      value={null}
                      onChange={(e: { value: string }) => pickModel(e.value)}
                      options={available.map((m) => ({
                        value: m.id,
                        label: m.name === m.id ? m.id : `${m.name} — ${m.id}`,
                        type: typeLabel(m.modelType),
                      }))}
                      itemTemplate={(o: { label: string; type: string }) => (
                        <div className="flex justify-between gap-3">
                          <span className="truncate">{o.label}</span>
                          <span className="text-xs text-ink-muted shrink-0">{o.type}</span>
                        </div>
                      )}
                      placeholder={`Pick from ${available.length} models this key can see`}
                      filter
                      filterBy="label,value"
                      virtualScrollerOptions={available.length > 50 ? { itemSize: 38 } : undefined}
                      data-testid="AiModelForm.ModelPicker"
                    />
                  )}
                  {listError && (
                    <span className="text-xs text-red-600" data-testid="AiModelForm.ListError">{listError}</span>
                  )}
                  <Controller
                    name="modelId"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={activeSpec?.modelIdPlaceholder || 'e.g., claude-opus-5'}
                        error={errors.modelId?.message}
                      />
                    )}
                  />
                  {/* Azure routes on the DEPLOYMENT you created, not the model's
                      own name, so the field is relabelled rather than left to be
                      guessed from a placeholder naming a different vendor. */}
                  {activeSpec?.modelIdHint && (
                    <span className="text-xs text-ink-muted">
                      {activeSpec.modelIdHint}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="displayName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Human-friendly model name"
                        error={errors.displayName?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Model Type
                  </label>
                  <Controller
                    name="modelType"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value || null}
                        onChange={(e: { value: string }) => field.onChange(e.value || '')}
                        options={modelTypeOptions}
                        placeholder="Select model type"
                        showClear
                      />
                    )}
                  />
                  {isEmbedding && (
                    <span className="text-xs text-ink-muted">
                      Returns vectors, not text. Called through the embeddings
                      endpoint rather than by prompts, so it does not appear in
                      prompt model pickers.
                    </span>
                  )}
                  {isDocument && (
                    <span className="text-xs text-ink-muted">
                      Takes a PDF or image and returns its text. Called through the
                      analyze-document endpoint rather than by prompts, so it does
                      not appear in prompt model pickers.
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Version
                  </label>
                  <Controller
                    name="version"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="e.g., 2024-10-22"
                        error={errors.version?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Icon Path
                  </label>
                  <Controller
                    name="iconPath"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="File or asset path to an icon"
                        error={errors.iconPath?.message}
                      />
                    )}
                  />
                </div>

                <div className="md:col-span-2 flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="What this model is for"
                        rows={3}
                        error={errors.description?.message}
                      />
                    )}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* Fine-tune lineage — what it was trained from and when */}
          {isFineTuned && (
            <div className="ds-card mb-4" data-testid="AiModelForm.Section.FineTune">
              <div className="px-4 py-3 border-b border-line">
                <div className="flex items-center gap-2">
                  <i className="pi pi-sitemap text-blue-600" />
                  <span className="text-sm font-medium text-ink">Fine-tune Details</span>
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Base Model <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="baseModelId"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          value={field.value || null}
                          onChange={(e: { value: string }) => pickBaseModel(e.value || '')}
                          options={baseModelOptions}
                          placeholder="Model this was fine-tuned from"
                          filter
                          data-testid="AiModelForm.BaseModel"
                        />
                      )}
                    />
                    {errors.baseModelId ? (
                      <span className="text-xs text-red-500">{errors.baseModelId.message}</span>
                    ) : (
                      <span className="text-xs text-ink-muted">Empty capabilities are copied from it.</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last Fine-tuned On
                    </label>
                    <Controller
                      name="trainedAt"
                      control={control}
                      render={({ field }) => (
                        <Calendar
                          value={field.value ?? null}
                          onChange={(e: { value?: Date | null }) => field.onChange(e.value ?? null)}
                          showTime
                          showIcon
                          hourFormat="24"
                          dateFormat="dd M yy"
                          maxDate={new Date()}
                          placeholder="When the last training run finished"
                          data-testid="AiModelForm.TrainedAt"
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Fine-tune Job ID
                    </label>
                    <Controller
                      name="fineTuneJobId"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder="e.g., ftjob-abc123" error={errors.fineTuneJobId?.message} />
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Training Status
                    </label>
                    <Controller
                      name="trainingStatus"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          value={field.value || null}
                          onChange={(e: { value: string }) => field.onChange(e.value || '')}
                          options={TRAINING_STATUS_OPTIONS}
                          placeholder="Select status"
                          showClear
                        />
                      )}
                    />
                  </div>

                  <div className="md:col-span-2 flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Training Dataset
                    </label>
                    <Controller
                      name="datasetRef"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder="File ID, path or name of the training data" error={errors.datasetRef?.message} />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Credentials */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-key text-blue-600" />
                <span className="text-sm font-medium text-ink">Credentials</span>
              </div>
            </div>
            <div className="p-4">
              <ModelCredentialsPanel
                providerType={selectedProvider}
                spec={activeSpec}
                value={credentials}
                onChange={(next) => { setCredentials(next); setCredentialsDirty(true); }}
                errors={credentialErrors}
                appId={selectedAppId}
              />
            </div>
          </div>

          {/* Capabilities */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-bolt text-blue-600" />
                <span className="text-sm font-medium text-ink">Capabilities</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Context Tokens
                  </label>
                  <Controller
                    name="maxContextTokens"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value ?? null}
                        onValueChange={(e) => field.onChange(e.value ?? null)}
                        placeholder="e.g., 200000"
                        useGrouping={false}
                        className="w-full"
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Output Tokens
                  </label>
                  <Controller
                    name="maxOutputTokens"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value ?? null}
                        onValueChange={(e) => field.onChange(e.value ?? null)}
                        placeholder="e.g., 8192"
                        useGrouping={false}
                        className="w-full"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {([
                  ['supportsSystemPrompt', 'System Prompt'],
                  ['supportsTools', 'Tools'],
                  ['supportsStreaming', 'Streaming'],
                  ['supportsJsonMode', 'JSON Mode'],
                  ['supportsVision', 'Vision'],
                ] as const).map(([name, label]) => (
                  <div key={name} className="flex items-center gap-2">
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <InputSwitch
                          checked={!!field.value}
                          onChange={(e: any) => field.onChange(e.value)}
                        />
                      )}
                    />
                    <label className="text-sm text-gray-700 dark:text-gray-300">{label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-dollar text-blue-600" />
                <span className="text-sm font-medium text-ink">
                  {isDocument ? 'Pricing (per 1,000 pages)' : 'Pricing (per 1M tokens)'}
                </span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Currency
                  </label>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="e.g., USD"
                        error={errors.currency?.message}
                      />
                    )}
                  />
                </div>

                {(isDocument
                  ? ([['perThousandPages', 'Per 1,000 pages']] as const)
                  : ([
                    ['inputPerM', 'Input / M'],
                    ['outputPerM', 'Output / M'],
                    ['cacheReadPerM', 'Cache Read / M'],
                    ['cacheWritePerM', 'Cache Write / M'],
                  ] as const)
                ).map(([name, label]) => (
                  <div key={name} className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          value={field.value ?? null}
                          onValueChange={(e) => field.onChange(e.value ?? null)}
                          mode="decimal"
                          minFractionDigits={0}
                          maxFractionDigits={4}
                          placeholder="0.00"
                          className="w-full"
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Parameters (paramsSchema) */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <i className="pi pi-sliders-h text-blue-600" />
                <span className="text-sm font-medium text-ink">Parameters (paramsSchema)</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Gemini takes temperature/topP/topK, which current Claude
                    models reject outright — paramsSchema decides what may be
                    sent at all, so seeding the wrong set breaks every call. */}
                {([
                  { label: 'Claude preset', rows: CLAUDE_PRESET, testId: 'ClaudePreset' },
                  { label: 'OpenAI preset', rows: OPENAI_PRESET, testId: 'OpenAiPreset' },
                  { label: 'Gemini preset', rows: GEMINI_PRESET, testId: 'GeminiPreset' },
                ]).map((preset) => (
                  <Button
                    key={preset.testId}
                    type="button"
                    label={preset.label}
                    severity="secondary"
                    outlined
                    size="small"
                    onClick={() => replaceParams(preset.rows)}
                    data-testid={`AiModelForm.Button.${preset.testId}`}
                  />
                ))}
                <Button
                  type="button"
                  label="Add row"
                  icon="pi pi-plus"
                  size="small"
                  onClick={() => appendParam({ name: '', default: '', min: '', max: '', enum: '' })}
                  data-testid="AiModelForm.Button.AddParam"
                />
              </div>
            </div>
            <div className="p-4">
              <p className="text-xs text-ink-muted mb-3">
                Declares which parameters this model accepts at run time. An empty schema
                makes the model reject every parameter — add rows (or use a preset) so Test
                Runs work. Provide an Enum (comma-separated) for choice params, or Min/Max
                for numeric ranges.
              </p>
              {paramFields.length === 0 ? (
                <p className="text-sm text-ink-muted">No parameters defined.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="hidden md:grid grid-cols-12 gap-2 text-xs text-ink-muted px-1">
                    <span className="col-span-3">Name</span>
                    <span className="col-span-2">Default</span>
                    <span className="col-span-2">Min</span>
                    <span className="col-span-2">Max</span>
                    <span className="col-span-2">Enum (csv)</span>
                    <span className="col-span-1" />
                  </div>
                  {paramFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-12 md:col-span-3">
                        <Controller
                          name={`paramsRows.${index}.name`}
                          control={control}
                          render={({ field: f }) => <Input {...f} placeholder="e.g., maxTokens" />}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Controller
                          name={`paramsRows.${index}.default`}
                          control={control}
                          render={({ field: f }) => <Input {...f} placeholder="default" />}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Controller
                          name={`paramsRows.${index}.min`}
                          control={control}
                          render={({ field: f }) => <Input {...f} placeholder="min" />}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Controller
                          name={`paramsRows.${index}.max`}
                          control={control}
                          render={({ field: f }) => <Input {...f} placeholder="max" />}
                        />
                      </div>
                      <div className="col-span-5 md:col-span-2">
                        <Controller
                          name={`paramsRows.${index}.enum`}
                          control={control}
                          render={({ field: f }) => <Input {...f} placeholder="a,b,c" />}
                        />
                      </div>
                      <div className="col-span-1 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => removeParam(index)}
                          className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          data-testid="AiModelForm.Button.RemoveParam"
                          aria-label="Remove parameter"
                        >
                          <i className="pi pi-trash text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Flags */}
          <div className="ds-card mb-4">
            <div className="px-4 py-3 border-b border-line">
              <div className="flex items-center gap-2">
                <i className="pi pi-flag text-blue-600" />
                <span className="text-sm font-medium text-ink">Flags</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {([
                  ['isDefault', 'Default'],
                  ['isActive', 'Active'],
                  ['isDeprecated', 'Deprecated'],
                ] as const).map(([name, label]) => {
                  return (
                    <div key={name} className="flex items-center gap-2">
                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) => (
                          <InputSwitch
                            checked={!!field.value}
                            onChange={(e: any) => field.onChange(e.value)}
                          />
                        )}
                      />
                      <label className="text-sm text-gray-700 dark:text-gray-300">
                        {label}
                        {/* Defaults are tracked per kind, so naming a default
                            embedding model does not unset the one prompts
                            resolve to. */}
                        {name === 'isDefault' && isEmbedding && (
                          <span className="text-xs text-gray-400"> embedding model</span>
                        )}
                        {name === 'isDefault' && isDocument && (
                          <span className="text-xs text-gray-400"> document model</span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Errors */}
          {Object.keys(errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <i className="pi pi-exclamation-circle text-red-600" />
              <span className="text-sm text-red-800 dark:text-red-200">
                Please fix the errors above before submitting.
              </span>
            </div>
          )}

          {/* Only ever shown on failure. A successful check is silent — the
              model is simply created, which is what the operator asked for. */}
          {connTest && !connTest.ok && (
            <div
              className="mb-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20"
              data-testid="AiModelForm.ConnectionTestResult"
            >
              <i className="pi pi-times-circle text-red-600 mt-0.5" />
              <div className="flex flex-col gap-0.5">
                {/* "rejected these credentials" was shown for EVERY failure,
                    including the ones where nothing ever reached the provider —
                    which sends the operator to re-read a key that was fine. */}
                <span className="text-sm font-medium text-red-800 dark:text-red-200">
                  {connTest.summary || 'The provider rejected these credentials'} — nothing was saved
                </span>
                {/* The provider's own words, plus the root cause the SDK buried. */}
                <span className="text-xs text-gray-600 dark:text-gray-300 break-words">
                  {connTest.error}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/ai-models')}
              disabled={testing || isSaving}
            />
            <Button
              type="submit"
              label={testing ? 'Checking credentials...'
                : isSaving ? 'Saving...'
                  : isEditMode ? 'Update' : 'Create'}
              icon={(testing || isSaving) ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              disabled={testing || isSaving}
              data-testid="AiModelForm.Button.Submit"
            />
          </div>
        </form>
      </div>

      {/* Leave Confirmation Dialog */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="AiModelForm.Dialog.Leave"
      />
    </>
  );
};

export default AiModelForm;
